import * as React from 'react';
import {
  useListInventoryLifecyclePreviewChildren,
  type InventoryLifecyclePreview, type InventoryLifecycleState, type InventoryLifecycleChild,
  type InventoryLifecycleIdentity
} from '@cdf/api-client-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { InventoryLifecycleHelp } from './inventory-lifecycle-help';
import { InventoryLifecycleSpaceBookings } from './inventory-lifecycle-space-bookings';
import { formatLifecycleTime } from '@/lib/inventory-lifecycle-time';
import { getErrorMessage } from '@/lib/utils';

export const lifecycleActionLabels = {
  deactivate: 'Close temporarily', retire: 'Retire permanently', reopen: 'Reopen',
  remove_schedule: 'Remove scheduled closure'
} as const;

function itemLabel(item: InventoryLifecycleIdentity) {
  return item.name.includes(item.code) ? item.name : item.name + ' (' + item.code + ')';
}

function ClosureSource({ state }: { state: InventoryLifecycleState }) {
  return <>
    {state.ownStatus !== 'active' ? <span>Closed individually ({state.ownStatus})</span> : null}
    {state.restrictions.filter(r => r.inherited).map(r => <span key={r.source.id + r.effectiveAt + r.reason}>
      {r.reason === 'booking_mode' ? 'Booking restricted by' : 'Closed by'} {r.source.level} {itemLabel(r.source)}
      {r.reason !== 'booking_mode' ? ' (' + r.status + ')' : ''}
    </span>)}
  </>;
}

export function InventoryLifecycleSummary({ state, fallback }: {
  state?: InventoryLifecycleState; fallback?: string;
}) {
  if (!state) return <Badge variant="secondary">{fallback ?? 'active'}</Badge>;
  return <div className="grid gap-1 text-xs">
    <Badge variant="outline">{state.effectiveStatus}</Badge>
    <ClosureSource state={state} />
    {state.pendingClosures.map(r => <span key={r.transitionId}>
      Scheduled {r.status === 'retired' ? 'retirement' : 'closure'}{r.inherited ? ' from ' + r.source.name : ''}: {formatLifecycleTime(r.effectiveAt, state.timezone)}
    </span>)}
  </div>;
}

const effectLabels = {
  newly_unavailable: 'Will become unavailable',
  already_unavailable: 'Already unavailable',
  restored: 'Temporary closure removed',
  remains_unavailable: 'Remains unavailable',
  unchanged: 'Unchanged'
} as const;

function ChildImpact({ child, preview }: { child: InventoryLifecycleChild; preview: InventoryLifecyclePreview }) {
  const priorRestriction = child.effect === 'already_unavailable';
  const remainingRestriction = child.effect === 'remains_unavailable';
  const summary = child.bookingImpact ?? (preview.bookingImpact.bookings?.total === 0
    ? { cancellationCount: 0, underwayCount: 0 } : undefined);
  return <li className="grid gap-3 rounded-md border p-3 sm:grid-cols-2">
    <div className="grid content-start gap-1">
      <p className="font-medium">{itemLabel(child.item)} <span className="text-muted-foreground text-xs">· {child.item.level}</span></p>
      <p className="text-sm">{effectLabels[child.effect]}</p>
      {priorRestriction || remainingRestriction ? <div className="grid gap-1 text-muted-foreground text-xs">
        <ClosureSource state={priorRestriction ? child.before : child.after} />
      </div> : null}
      <details className="text-muted-foreground text-xs">
        <summary className="cursor-pointer">Location path</summary>
        <p className="pt-1">{child.item.path.join(' / ')}</p>
      </details>
    </div>
    {child.item.level === 'space' && preview.bookingImpact.available ? <InventoryLifecycleSpaceBookings
      previewId={preview.previewId} space={child.item} timezone={preview.timezone} summary={summary}
    /> : null}
  </li>;
}

/** Inventory and assistant cards share a paginated child review with per-space booking pages. */
export function InventoryLifecycleImpact({ preview, showContext = true }: {
  preview: InventoryLifecyclePreview; showContext?: boolean;
}) {
  const [childOffset, setChildOffset] = React.useState(0);
  const childPage = useListInventoryLifecyclePreviewChildren(preview.previewId, { limit: 20, offset: childOffset },
    { query: { enabled: childOffset > 0, retry: false } });
  const children = childOffset ? childPage.data?.items ?? [] : preview.children.items.slice(0, 20);
  const closing = preview.action === 'deactivate' || preview.action === 'retire';
  const cancellationCount = preview.bookingImpact.cancellationCount ?? 0;
  const underwayCount = preview.bookingImpact.underwayCount ?? 0;
  return <section className="grid gap-3 text-sm" aria-label="Lifecycle impact review">
    {showContext ? <div>
      <p className="font-medium">{lifecycleActionLabels[preview.action]} — {itemLabel(preview.selectedBefore.item)}</p>
      <p className="text-muted-foreground">{closing ? 'Closure starts' : 'Change takes effect'}: {formatLifecycleTime(preview.effectiveAt, preview.timezone)}</p>
    </div> : null}
    <div className="flex items-center justify-between gap-2">
      <h4 className="font-medium">{preview.children.total > 0 ? 'Affected children and bookings' : 'Impact'}</h4>
      <InventoryLifecycleHelp label="About this status change">
        <div className="grid gap-2">
          <p>{preview.action === 'retire' ? 'Retirement is permanent and retains history and reporting. It has no routine reopening action.'
            : closing ? 'Temporary closures can be reopened.' : 'Only this closure is removed; independent child closures and other parent restrictions remain.'}</p>
          <p>Child statuses, booking modes and assignments are preserved.</p>
          {closing ? <p>Bookings listed for cancellation are cancelled when you confirm, even for a future closure.</p> : null}
          <p>Cancelled bookings stay cancelled after reopening or removing a schedule.</p>
        </div>
      </InventoryLifecycleHelp>
    </div>
    {preview.action === 'retire' ? <p className="font-medium">Retirement is permanent.</p> : null}
    {!preview.bookingImpact.available ? <p role="alert">Booking impact is unavailable. Confirmation is disabled.</p> : <>
      <p>{cancellationCount === 0 ? 'No bookings to cancel.' : cancellationCount + (cancellationCount === 1 ? ' booking will' : ' bookings will') + ' be cancelled when you confirm.'}</p>
      {underwayCount > 0 ? <p>{underwayCount} underway bookings may finish at their existing end time.</p> : null}
    </>}
    {!closing ? <p>Independent child closures and other parent restrictions remain. Cancelled bookings stay cancelled.</p> : null}
    {preview.children.total > 0 ? <>
      <div className="flex flex-wrap gap-2">
        {(['building', 'floor', 'zone', 'space'] as const).filter(level => (preview.counts[level] ?? 0) > 0).map(level =>
          <Badge variant="outline" key={level}>{level}: {preview.counts[level]}</Badge>)}
      </div>
      <p className="text-muted-foreground text-xs">
        {(['newly_unavailable', 'already_unavailable', 'restored', 'remains_unavailable'] as const)
          .filter(effect => (preview.counts[effect] ?? 0) > 0)
          .map(effect => preview.counts[effect] + ' ' + ({
            newly_unavailable: 'become unavailable', already_unavailable: 'already unavailable',
            restored: 'restored', remains_unavailable: 'remain unavailable'
          })[effect]).join(' · ')}
      </p>
      {childOffset > 0 && childPage.isFetching ? <p role="status">Loading children…</p> : null}
      {childPage.error ? <p role="alert" className="text-destructive">{getErrorMessage(childPage.error)}</p> : null}
      <ul className="grid gap-2">
        {children.map(child => <ChildImpact key={child.item.id} child={child} preview={preview} />)}
      </ul>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>{childOffset + 1}–{Math.min(childOffset + 20, preview.children.total)} of {preview.children.total} children</span>
        <Button type="button" variant="outline" size="sm" disabled={childOffset === 0}
          onClick={() => setChildOffset(value => Math.max(0, value - 20))} aria-label="Previous children">Previous</Button>
        <Button type="button" variant="outline" size="sm" disabled={childOffset + 20 >= preview.children.total}
          onClick={() => setChildOffset(value => value + 20)} aria-label="Next children">Next</Button>
      </div>
    </> : <p>No affected children.</p>}
    {preview.selectedBefore.item.level === 'space' && preview.bookingImpact.available ? <InventoryLifecycleSpaceBookings
      previewId={preview.previewId} space={preview.selectedBefore.item} timezone={preview.timezone}
      summary={{ cancellationCount, underwayCount }} /> : null}
    {!closing ? <InventoryLifecycleSummary state={preview.selectedAfter} /> : null}
  </section>;
}
